import React from 'react';
import { Box, Typography, Container, Button, Grid, Stack, Chip, Divider } from '@mui/material';
import { motion } from 'framer-motion';
import Typewriter from 'typewriter-effect';
import { styled } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import DownloadIcon from '@mui/icons-material/Download';
import { IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom'; // Add this import

const HeroSection = styled(Box)(({ theme }) => ({
  minHeight: '90vh',
  display: 'flex',
  alignItems: 'center',
  background: theme.palette.background.default,
  position: 'relative',
  overflow: 'hidden',
  paddingTop: { xs: '80px', sm: '60px', md: '0' },
}));

const BackgroundPattern = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `radial-gradient(circle at 10% 20%, ${theme.palette.primary.light}10 5%, transparent 5%), 
               radial-gradient(circle at 90% 80%, ${theme.palette.secondary.main}10 5%, transparent 5%)`,
  backgroundSize: '80px 80px',
  opacity: 0.3,
  zIndex: 0,
  pointerEvents: 'none',
}));

const ProfileCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  position: 'relative',
  maxWidth: '100%',
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 6px 25px rgba(0, 0, 0, 0.1)',
  },
}));

const JourneyTimeline = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(2, 0),
  height: '260px', 
  overflowY: 'auto',
  marginRight: theme.spacing(-1),
  paddingRight: theme.spacing(1),
  flexGrow: 1,
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.divider} transparent`,
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.divider,
    borderRadius: '6px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.palette.primary.light,
  }
}));

const TimelineItem = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingLeft: theme.spacing(4),
  marginBottom: theme.spacing(2.5),
  '&:last-child': {
    marginBottom: theme.spacing(1),
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '6px',
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    border: `2px solid ${theme.palette.background.paper}`,
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}30`,
    zIndex: 2,
    transition: 'transform 0.2s ease',
  },
  '&:hover::before': {
    transform: 'scale(1.2)',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    left: '7px',
    top: '20px',
    bottom: 0,
    width: '2px',
    backgroundColor: theme.palette.primary.main,
    opacity: 0.25,
    zIndex: 1,
    display: 'block'
  },
  '&:last-child::after': {
    display: 'none',
  }
}));

const HighlightedText = styled('span')(({ theme }) => ({
  color: theme.palette.secondary.main,
  position: 'relative',
  fontWeight: 700,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: theme.palette.secondary.main,
  textShadow: 'none',
  padding: '0 4px',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '8px',
    backgroundColor: `${theme.palette.secondary.main}15`,
    zIndex: -1,
  }
}));

const ActionButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  fontWeight: 500,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    '& .MuiButton-endIcon': {
      transform: 'translateX(3px)',
    }
  },
  '& .MuiButton-endIcon': {
    transition: 'transform 0.2s ease',
  }
}));

const SocialButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  borderRadius: '50%',
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.primary.main,
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
  },
}));

const SkillChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(100, 181, 246, 0.1)' 
    : 'rgba(46, 80, 119, 0.08)',
  color: theme.palette.primary.main,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.75rem',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(100, 181, 246, 0.2)' 
      : 'rgba(46, 80, 119, 0.15)',
    transform: 'translateY(-2px)',
  },
}));

const ResumeButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  fontWeight: 500,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
  marginRight: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
  },
}));

const TypewriterHighlight = styled('span')(({ theme }) => ({
  color: theme.palette.secondary.main,
  fontWeight: 600,
}));

const Home = () => {
  const navigate = useNavigate(); // Add this line
  
  const containerAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
  };

  const itemAnimation = {
    hidden: { y: 10, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 50,
        damping: 10,
      },
    },
  };

  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.8 }
    },
  };

  const professionalJourney = [
    {
      year: '2023 - _',
      title: 'Senior Software Engineer',
      company: 'Petco.com',
      location: 'Remote, India',
      description: 'Leading backend development team, architecting scalable cloud solutions, and Migrating the WebSphere Commerce (WCS) legacy codebase to a Spring Boot microservices architecture.',
      achievements: [
        'Led the integration of Kafka consumer groups, achieving real‑time data synchronization and reducing SKU update latency from hours to <5s.',
        'Reduced system latency by 40% through optimization of database queries and caching mechanisms',
        'Led a team of 8 engineers to deliver critical platform migration with zero downtime',
        'Designed and implemented a scalable microservices‑based email notification system, improving delivery efficiency.',
      ],
      technologies: ['Java', 'Spring Boot', 'Kafka', 'PostgreSQL']
    },
    {
      year: '2022 - 2023',
      title: 'Software Engineer',
      company: 'Ninjacart',
      location: 'Bengaluru, India',
      description: 'worked on an in-house fintech product which enables credit facility for eligible traders on the Ninjacart apps',
      achievements: [
        'Enhanced a payment gateway by integrating third‑party APIs, enabling 50K+ daily inter‑bank transactions with 99.9% uptime.',
        'Developed a Thymeleaf‑based loan application module, cutting processing time by 35%',
        'Improved API response times by 45% through code optimization and caching strategies'
      ],
      technologies: ['Java', 'Spring Boot', 'MySQL', 'Docker']
    },
    {
      year: '2021 - 2022',
      title: 'System Engineer',
      company: 'Tata Consultancy Services',
      location: 'Gurgaon, India',
      description: 'Worked For Quest Diagnostics, a leading healthcare company',
      achievements: [
        'Implemented a RESTful API for patient data access, reducing latency by 30%',
        'Developed a Java‑based web application for managing patient Billing, improving data accuracy by 20%',
      ],
      technologies: ['Java', 'Spring Boot', 'MySQL', 'Docker']
    },
    {
      year: '2020 - 2021',
      title: 'Software Engineer Trainee',
      company: 'Tata Consultancy Services',
      location: 'Gurgaon, India',
      description: 'Worked on a project for a leading healthcare company',
      achievements: [
        'Gained knowledge of Java, SQL, and Object Oriented Programming',
        'Gained knowledge of Software Engineering, Data Structures, Algorithms, Operating Systems, Computer Networks, and Database Management Systems',
      ],
      technologies: ['Java', 'SQL', 'Object Oriented Programming', 'Data Structures', 'Algorithms']
    },
    {
      year: '2016 - 2020',
      title: 'Under Graduate Student',
      company: 'Uttarakhand Technical University  ',
      location: 'Dehradun, India',
      description: 'Studied Computer Science and Engineering',
      achievements: [
        'Learned the basics of Software Engineering, Data Structures, Algorithms, Operating Systems, Computer Networks, and Database Management Systems'
      ],
      technologies: ['Software Engineering', 'Data Structures', 'Algorithms', 'Operating Systems', 'Computer Networks', 'Database Management Systems']
    }
  ];

  const skills = [
    { name: 'Java', level: 90 },
    { name: 'Spring Boot', level: 90 },
    { name: 'Kafka', level: 90 },
    { name: 'PostgreSQL', level: 90 },
    { name: 'Git', level: 90 },
    { name: 'Docker', level: 90 },
    { name: 'Microservices',level: 90},
    { name: 'RESTful APIs',level: 90},
    { name: 'OOPs',level: 90},
    { name: 'Data Structures',level: 90},
    { name: 'Algorithms',level: 90},
  ];


  return (
    <HeroSection>
      <BackgroundPattern />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: { xs: 4, sm: 2, md: 0 } }}>
        <Grid 
          container 
          spacing={{ xs: 4, md: 5 }} 
          alignItems="stretch"
          sx={{ minHeight: { md: '80vh' } }}
        >
          <Grid item xs={12} md={6}>
            <motion.div
              variants={containerAnimation}
              initial="hidden"
              animate="visible"
              style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
            >
              <Box sx={{ mb: 4, mt : { xs: 8, sm: 4 } }}>
                <motion.div variants={itemAnimation}>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 500,
                      mb: 1,
                      fontFamily: '"Inter", sans-serif',
                      color: theme => theme.palette.text.primary,
                      position: 'relative',
                      width: '100%',
                      overflowWrap: 'break-word',
                      wordBreak: 'break-word',
                      fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                      mt: { xs: 2, sm: 0 },
                      '& .Typewriter': {
                        width: '100%',
                        display: 'block'
                      }
                    }}
                  >
                    <Typewriter
                      options={{
                        strings: [
                          '<span style="color: #ff6b6b; font-weight: 600;">#</span> Hello, I am <span style="color: #ff6b6b; font-weight: 600;">Avinash Kumar</span>',
                          '<span style="color: #ff6b6b; font-weight: 600;">#</span> I craft <span style="color: #ff6b6b; font-weight: 600;">high-performance</span> backend systems',
                          '<span style="color: #ff6b6b; font-weight: 600;">#</span> I architect <span style="color: #ff6b6b; font-weight: 600;">scalable</span> cloud solutions',
                          '<span style="color: #ff6b6b; font-weight: 600;">#</span> I solve <span style="color: #ff6b6b; font-weight: 600;">complex</span> technical challenges',
                          '<span style="color: #ff6b6b; font-weight: 600;">#</span> I build with <span style="color: #ff6b6b; font-weight: 600;">reliability</span> in mind',
                          '<span style="color: #ff6b6b; font-weight: 600;">#</span> I optimize for <span style="color: #ff6b6b; font-weight: 600;">speed</span> and <span style="color: #ff6b6b; font-weight: 600;">efficiency</span>',
                          '<span style="color: #ff6b6b; font-weight: 600;">#</span> I transform <span style="color: #ff6b6b; font-weight: 600;">ideas</span> into <span style="color: #ff6b6b; font-weight: 600;">solutions</span>',
                        ],
                        autoStart: true,
                        loop: true,
                        delay: 40,
                        deleteSpeed: 20,
                        cursor: '█',
                        cursorClassName: 'typewriter-cursor',
                        wrapperClassName: 'typewriter-wrapper',
                        html: true,
                      }}
                    />
                  </Typography>
                </motion.div>
                
                <Box sx={{
                  '& .typewriter-cursor': {
                    color: theme => theme.palette.secondary.main,
                    animation: 'blink 0.7s infinite',
                    '@keyframes blink': {
                      '0%': { opacity: 0 },
                      '50%': { opacity: 1 },
                      '100%': { opacity: 0 },
                    },
                  },
                  '& .typewriter-wrapper': {
                    display: 'inline-block',
                  },
                  mb: 2,
                }} />
                
                <motion.div variants={itemAnimation}>
                  <Typography 
                    variant="h1" 
                    gutterBottom 
                    component="div"
                    sx={{ 
                      fontWeight: 700,
                      mb: 2,
                      fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3.5rem' },
                      lineHeight: 1.2,
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      hyphens: 'auto'
                    }}
                  >
                    <Box 
                      sx={{ 
                        backgroundImage: theme => theme.palette.mode === 'dark' 
                          ? 'linear-gradient(45deg, #fff 30%, #f0f0f0 90%)' 
                          : 'linear-gradient(45deg, #1a2a3a 30%, #2e5077 90%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        textShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        display: 'inline',
                      }}
                    >
                      Turning Complex Challenges into{' '}
                    </Box>
                    <HighlightedText>Elegant Solutions</HighlightedText>
                  </Typography>
                </motion.div>
                
                <motion.div variants={itemAnimation}>
                  <Typography 
                    variant="body1" 
                    color="text.secondary" 
                    paragraph 
                    sx={{ 
                      mb: 3,
                      fontSize: { xs: '0.9rem', sm: '1rem' },
                      maxWidth: '550px',
                      lineHeight: 1.6,
                      px: { xs: 1, sm: 0 }
                    }}
                  >
                    I build robust, scalable software solutions with a focus on cloud architecture 
                    and distributed systems. Passionate about solving complex problems and creating 
                    efficient, maintainable code.
                  </Typography>
                </motion.div>
                
                <motion.div variants={itemAnimation} sx={{ mt: 4 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3, width: { xs: '100%', sm: 'auto' } }}>
                    <ActionButton
                      variant="contained"
                      color="primary"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => navigate('/blog')}
                    >
                      Read My Articles ✨
                    </ActionButton>
                    <ActionButton
                      variant="outlined"
                      color="primary"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                    >
                      Get in Touch
                    </ActionButton>
                  </Stack>
                </motion.div>
                
                <motion.div variants={itemAnimation}>
                  <Stack direction="row" spacing={1.5} sx={{ mt: 4 }}>
                    <SocialButton aria-label="github">
                      <GitHubIcon fontSize="small" />
                    </SocialButton>
                    <SocialButton aria-label="linkedin">
                      <LinkedInIcon fontSize="small" />
                    </SocialButton>
                  </Stack>
                </motion.div>
              </Box>
            </motion.div>
          </Grid>
          
          <Grid item xs={12} md={6} sx={{ display: 'flex',mt : { xs: 8, sm: 4 } }}>
            <motion.div
              variants={fadeIn}
              initial="hidden"
              animate="visible"
              style={{ width: '100%' }}
            >
              <ProfileCard>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', }}>
                  <Typography variant="h5" gutterBottom sx={{ 
                    mb: 2, 
                    fontWeight: 600, 
                    color: 'primary.main',
                    borderBottom: theme => `1px solid ${theme.palette.divider}`,
                    pb: 1
                  }}>
                    My Professional Journey
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<DownloadIcon />}
                    href="/files/avinash_kumar_resume.pdf"
                    download
                    component="a"
                    target="_blank"
                    sx={{
                      borderRadius: 1,
                      fontSize: '0.8rem',
                      mb: 3,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        color: 'primary.main',
                        borderColor: 'primary.main',
                        backgroundColor: 'primary.main',
                        color: 'white',
                      },
                    }}
                  >
                    Download CV
                  </Button>
                </Box>
                
                <JourneyTimeline>
                  {professionalJourney.map((item, index) => (
                    <TimelineItem key={index}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                        <Typography 
                          variant="caption" 
                          color="primary" 
                          sx={{ 
                            fontWeight: 700, 
                            fontSize: '0.7rem',
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase',
                            backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.08)',
                            padding: '2px 8px',
                            borderRadius: '4px',
                          }}
                        >
                          {item.year}
                        </Typography>
                        <Typography 
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            fontSize: '0.7rem',
                            fontStyle: 'italic',
                            display: { xs: 'none', sm: 'block' }
                          }}
                        >
                          {item.location}
                        </Typography>
                      </Box>
                      <Typography 
                        variant="subtitle2" 
                        gutterBottom 
                        sx={{ 
                          fontWeight: 600, 
                          mb: 0.3,
                          display: 'flex',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                        }}
                      >
                        {item.title} 
                        <Typography 
                          component="span" 
                          variant="caption" 
                          color="text.secondary" 
                          sx={{ 
                            ml: 0.5,
                            fontStyle: 'italic',
                          }}
                        >
                          @ {item.company}
                        </Typography>
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          fontSize: '0.85rem',
                          lineHeight: 1.5,
                          opacity: 0.9,
                          mb: 1,
                        }}
                      >
                        {item.description}
                      </Typography>
                      
                      <Box sx={{ 
                        pl: 1, 
                        mb: 1,
                        borderLeft: theme => `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)'}`,
                      }}>
                        {item.achievements.map((achievement, i) => (
                          <Typography 
                            key={i}
                            variant="body2" 
                            sx={{ 
                              fontSize: '0.8rem',
                              lineHeight: 1.4,
                              mb: 0.5,
                              position: 'relative',
                              pl: 1.5,
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: '0.5em',
                                width: '6px',
                                height: '6px',
                                backgroundColor: theme => theme.palette.secondary.main,
                                borderRadius: '50%',
                              }
                            }}
                          >
                            {achievement}
                          </Typography>
                        ))}
                      </Box>
                      
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {item.technologies.map((tech, i) => (
                          <Chip
                            key={i}
                            label={tech}
                            size="small"
                            sx={{
                              height: '20px',
                              fontSize: '0.65rem',
                              backgroundColor: theme => theme.palette.mode === 'dark' 
                                ? 'rgba(255, 255, 255, 0.07)' 
                                : 'rgba(0, 0, 0, 0.05)',
                              color: 'text.secondary',
                              '& .MuiChip-label': {
                                px: 1,
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </TimelineItem>
                  ))}
                </JourneyTimeline>
                
                <Divider sx={{ my: 2 }} />
{/*                 
                <Typography variant="h6" gutterBottom sx={{ 
                  fontWeight: 600, 
                  mt: 1, 
                  color: 'primary.main',
                  borderBottom: theme => `1px solid ${theme.palette.divider}`,
                  pb: 1,
                  mb: 2
                }}>
                  Technical Skills
                </Typography>
                
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {skills.map((skill, index) => (
                    <SkillChip 
                      key={index} 
                      label={skill.name}
                      size="small"
                    />
                  ))}
                </Box> */}
              </ProfileCard>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </HeroSection>
  );
};

export default Home;